import { Fragment, useCallback, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Button from 'components/revelo-ui/content/button';
import { useFormat } from 'helpers/hooks/useFormat';
import { useDarkMode } from 'frontastic';
import XIcon from 'components/icons/icon-x';

export interface Props {
  open?: boolean;
  onClose?: () => void;
  onConfirm?: () => Promise<unknown>;
}

const DeleteConfirmationModal: React.FC<Props> = ({ open, onClose, onConfirm }) => {
  const { mode, theme } = useDarkMode();
  const { formatMessage } = useFormat({ name: 'common' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });

  const [processing, setProcessing] = useState(false);

  const handleConfirm = useCallback(async () => {
    setProcessing(true);
    await onConfirm?.();
    setProcessing(false);
    onClose?.();
  }, [onConfirm]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog className={`${mode} ${theme} default fixed inset-0 z-10 overflow-y-auto`} onClose={onClose}>
        <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-left sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="absolute inset-0" onClick={onClose}>
              <div className="absolute left-1/2 top-1/2 w-auto min-w-80 -translate-x-1/2 -translate-y-1/2 overflow-hidden bg-white px-4 pb-4 pt-5 text-left transition-all sm:my-8 sm:p-6">
                <div className="flex items-center justify-between gap-4">
                  <Dialog.Title className="font-headline text-xl text-neutral-950">
                    {formatAccountMessage({
                      id: 'notification.confirm.delete',
                      defaultMessage: 'Bike-Alarm löschen?',
                    })}
                  </Dialog.Title>
                  <button aria-label={'Dialog schließen'} onClick={onClose}>
                    <XIcon className="size-6" />
                  </button>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-4 sm:mt-6">
                  <Button style="hollow" onClick={onClose} disabled={processing} minWidth={false}>
                    Zurück
                  </Button>
                  <Button style="primary" onClick={handleConfirm} disabled={processing} minWidth={false}>
                    {formatMessage({ id: 'delete', defaultMessage: 'Delete' })}
                  </Button>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default DeleteConfirmationModal;
